import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon } from '@persona-id/design-tokens/icons';
import produce from 'immer';
import { debounce, isEqual, keyBy } from 'lodash/fp';
import {
  memo,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router-dom-v5-compat';
import styled, { css } from 'styled-components';

import EditorNavItemTooltip from 'dashboard/apps/InquiryTemplateEditor//EditorNavItemTooltip';
import CombinedStepPanel from 'dashboard/apps/InquiryTemplateEditor/CombinedStepPanel';
import DeviceHandoffPanel from 'dashboard/apps/InquiryTemplateEditor/DeviceHandoffPanel';
import EditorNavItemGroup, {
  GroupScrollItemProps,
} from 'dashboard/apps/InquiryTemplateEditor/EditorNavItemGroup/EditorNavItemGroup';
import EditorNavItemScroll, {
  EditorNavItemScrollProps,
} from 'dashboard/apps/InquiryTemplateEditor/EditorNavItemGroup/EditorNavItemScroll';
import useHasAccessForTierDeterminator from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasAccessForTierDeterminator';
import useIsEditableWithOptions from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import { buildFieldReferenceMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateConfigEditor/utils';
import {
  INQUIRY_TEMPLATE_EDITOR_FORM_NAME,
  SCREEN_CONTAINER_TO_PARENT_RATIO,
  SCREEN_EDITOR_STEP_PATH,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { deviceHandoffPages } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/pages';
import { getScreenDimensionsLiveUpdated } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/selectors/screenDimensions';
import {
  ScreenDimensions,
  StepPreviewMap,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import InquiryTemplateEditorToolbar, {
  TOOLBAR_HEIGHT,
  TOOLBAR_TRANSITION,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditorToolbar';
import { FlowTreeFormValues } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/types';
import { findComponentConfigPath } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import { NONDELETABLE_COMPONENTS } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-constants';
import { ComponentValidationErrorsMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-validations/types';
import { uiStepTotalNumComponentsWithRulesLogicOnConfig } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/utils';
import ComponentPanel from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentPanel';
import ConfirmDeleteFieldModal from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ConfirmDeleteFieldModal';
import ConfirmDeleteTransitionComponentModal from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ConfirmDeleteTransitionComponentModal';
import { setComponentPanelSetActiveTab } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/hooks/useComponentEditorComponentPanelSettings';
import ScreenComponentList from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ScreenComponentList';
import ScreenEditorComponentLibrary from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ScreenEditorComponentLibrary';
import ScreenSectionsPanel from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ScreenSectionsPanel';
import {
  isSelectedComponentTab,
  type SelectedComponentAndStep,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import {
  addComponentsAtIndex,
  configureComponentForCopy,
  configureComponentForPaste,
  configureFieldSchemaForPaste,
  copyComponentErrorMessage,
  duplicateTransitionComponentTargetExists,
  getPasteInsertionConfigs,
  isCurrentComponentBIPASubmitTrigger,
  isTransitionComponentTargetSet,
  pasteComponentErrorMessage,
  removeComponentFromPath,
  unnestComponentsAtPath,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/utils';
import ScreenPreviews from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/ScreenPreviews';
import { ScreenItemConfig } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/types';
import UnusuableScreenEditorWarning from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/UnusableScreenWarning';
import {
  getComponentFieldKeys,
  getCurrentComponent,
  isDeviceHandoffPageKey,
  mapToNavItemConfig,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import { scaleScreenDimensionsByFactor } from 'dashboard/apps/InquiryTemplateEditor/utils/screen-dimensions';
import {
  backStepTargetOptions,
  isInitialStep,
  uiStepAllComponentConfigs,
} from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import {
  CombinedStepConfig,
  InquiryTemplateVersion,
  InquiryTemplateVersionCombinedStep,
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
  isCombinedStepConfig,
  isIntegrationStepConfig,
  isUiStepConfig,
} from 'dashboard/models/inquiry-template-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import {
  clearMaxEditorScreenDimensions,
  refreshComponentPreview,
  updateMaxEditorScreenDimensions,
} from 'dashboard/redux/thunk-actions/inquiry-template';
import Button from 'design-system/components/Button';
import Select, { Option } from 'design-system/components/Select';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { Heading, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { TransitionDuration } from 'design-system/variables/transitions';
import { LocalStorageKey } from 'lib/clients/StorageClient';
import { GOVERNMENT_ID_DESIGN_VERSION } from 'lib/constants/governmentIds';
import { INQUIRY_LANGUAGES_ENGLISH, InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { FieldSchema } from 'lib/fields';
import useHasScrolled from 'lib/hooks/useHasScrolled';
import {
  PERMITTED_DOCUMENT_TAGS,
  useKeydownCopyListener,
  useKeydownDeleteListener,
  useKeyDownPasteListener,
} from 'lib/hooks/useKeyListener';
import useLocalStorage from 'lib/hooks/useLocalStorage';
import useMeasureText from 'lib/hooks/useMeasureText';
import useToggle from 'lib/hooks/useToggle';
import {
  CombinedStepClientData,
  isCombinedStep,
  StepPreview,
  StepUi,
} from 'lib/interfaces/StepsConfig';
import {
  InputMultiFieldComponentFieldConfig,
  isUiFormComponentConfig,
  isUiFormMultiFieldComponentConfig,
  isUiParentComponentConfig,
  isUiTransitionComponentConfig,
  UiComponentConfig,
  UiComponentConfigType,
} from 'lib/interfaces/ui-components';
import { captureMessage } from 'lib/reporting';

// there are multiple tabs in the screen editor nav, but this width exactly fits the component library grid
const SCREEN_EDITOR_NAV_CONTAINER_WIDTH = 272;
export const SCREEN_NAV_CONTAINER_PADDING = 48;
// max viewport width of (1250) - width of main side navigation (250)
const MAX_USABLE_EDITOR_WIDTH = 1000;
// used for offsetElementId when rendering popover panels (e.g translations, options)
export const SCREEN_EDITOR_CONTROL_PANEL_ID = 'fle-screen-editor-control-panel__container';
export const DISPLAY_FOCUS_SEARCH_PARAM = 'displayFocus';

// Hook to render UnusuableScreenEditorWarning when screen editor container
// is below the MAX_USUABLE_EDITOR_WIDTH size
const useIsElementBelowSize = ({
  element,
  breakpoint,
}: {
  element: HTMLElement;
  breakpoint: number;
}) => {
  const [elementIsBelowSize, setElementIsBelowSize] = useState(false);

  useEffect(() => {
    if (element != null) {
      const resizeObserver = new ResizeObserver((entries) => {
        // only watching one element for now
        const watchedElement = entries[0];
        const shouldSetBelow = watchedElement.contentBoxSize[0].inlineSize < breakpoint;
        if (shouldSetBelow !== elementIsBelowSize) {
          setElementIsBelowSize(shouldSetBelow);
        }
      });

      resizeObserver.observe(element);
      return () => {
        resizeObserver.unobserve(element);
      };
    }
  }, [element, breakpoint, elementIsBelowSize]);

  return [elementIsBelowSize];
};

const RootContainer = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
`;

const ContentContainer = styled.div`
  display: flex;
  height: calc(100% - ${TOOLBAR_HEIGHT}px);
`;

const ScreensPanelContainer = styled.div<{ visible: boolean }>`
  background-color: ${({ theme }) => theme.color.background.primary};
  border-right: 1px solid ${({ theme }) => theme.color.border.primary};
  width: ${SCREEN_EDITOR_NAV_CONTAINER_WIDTH}px;
  max-width: ${SCREEN_EDITOR_NAV_CONTAINER_WIDTH}px;
  flex: none;
  overflow: hidden;
  transition:
    flex ${TOOLBAR_TRANSITION},
    transform ${TOOLBAR_TRANSITION};
  flex: ${({ visible }) => (visible ? `1 0 ${SCREEN_EDITOR_NAV_CONTAINER_WIDTH}px` : '1 0 0px')};
  transform: ${({ visible }) => `translateX(${visible ? '0px' : '-100%'})`};
`;

const ScreenPanelHeader = styled.div<{ hasScrolledContent?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${Spacing[4]} ${Spacing[4]} ${Spacing[3]};
  box-shadow: ${({ hasScrolledContent, theme }) =>
    hasScrolledContent ? `0px 1px 0px 0px ${theme.color.border.primary}}` : 'none'};
  z-index: 1;
  transition: box-shadow ${TransitionDuration.Fast} ease-in;
`;

const ScreenPanelHeaderInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SelectWrapper = styled.div`
  max-width: 100px;
`;

const ScreensPanelHeading = styled(Heading).attrs(() => ({ size: 'sm' }))`
  display: inline-block;
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  font-weight: ${({ theme }) => theme.text.paragraph.lgBold.fontWeight};
  line-height: ${({ theme }) => theme.text.paragraph.md.lineHeight};
`;

const NavItemContainer = styled.div<{ overflowScroll?: boolean }>`
  padding: 0 ${Spacing[3]} ${Spacing[3]} ${Spacing[3]};
  ${({ overflowScroll }) =>
    overflowScroll
      ? css`
          max-height: 100%;
          overflow: auto;
        `
      : ``}
`;

const ScreenNavItem = styled(EditorNavItemScroll)`
  margin-top: 0px;
`;

const ControlPanelContainer = styled.div<{ visible: boolean }>`
  background-color: ${({ theme }) => theme.color.background.primary};
  border-left: 1px solid ${({ theme }) => theme.color.border.primary};
  width: 400px;
  max-width: 400px;
  flex: 0 0 400px;
  overflow-y: auto;
  overflow-x: hidden;
  transition: flex ${TOOLBAR_TRANSITION};
  flex: ${({ visible }) => (visible ? '1 0 400px' : '1 0 0px')};
  z-index: 1;
`;

const StyledTabBar = styled(TabBar)`
  padding: ${Spacing[2]} ${Spacing[2]} 0 ${Spacing[4]};
  border-bottom-width: 1px;
  height: auto;
`;

const ScreenEditorLeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: ${SCREEN_EDITOR_NAV_CONTAINER_WIDTH}px;
`;

const leftPanelSectionCSS = css`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  max-height: calc(50% - 20px);
`;

const ScreenComponentsSection = styled.div`
  ${leftPanelSectionCSS};
`;

const ScreenNavigationContainer = styled.div<{ hasSelectedStep?: boolean }>`
  ${leftPanelSectionCSS};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};
`;

const ComponentItemContainer = styled.div`
  padding: 0 ${Spacing[3]};
  margin-bottom: ${Spacing[3]};
  flex-grow: 1;
  overflow: auto;
`;

const ComponentItemTextContainer = styled.div`
  flex-grow: 1;
  padding: 0 ${Spacing[4]};
`;

const ScreenNavContainer = memo(
  (
    props: EditorNavItemScrollProps & {
      stepIndex?: number;
      placeholder: string;
      editDisabled?: boolean;
    },
  ) => {
    const textWidth = useMeasureText({ text: props.label });
    const useRenamable = props.stepIndex != null;

    return (
      <EditorNavItemTooltip
        shouldTriggerTooltip={
          textWidth > SCREEN_EDITOR_NAV_CONTAINER_WIDTH - SCREEN_NAV_CONTAINER_PADDING
        }
        trigger={
          <ScreenNavItem
            {...props}
            useRenameable={useRenamable}
            form={INQUIRY_TEMPLATE_EDITOR_FORM_NAME}
            path={`steps.${props.stepIndex}.name-display`}
            title={props.placeholder}
            displayPlaceholder={props.label}
          />
        }
        content={props.label}
      />
    );
  },
);

ScreenNavContainer.displayName = 'InquiryTemplateScreenEditor__ScreenNavContainer';

export type SelectedStep = Omit<ScreenItemConfig, 'items'>;
export type ComponentCopyData = {
  inquiryTemplateVersionId: string;
  component: UiComponentConfig;
};

interface InquiryTemplateScreenEditorProps {
  inquiryTemplate: InquiryTemplate;
  inquiryTemplateVersion: InquiryTemplateVersion;
  stepsPreviewMap: StepPreviewMap;
  pages: ReactElement[];
  baseRoutePath: string;
  currentLocale: InquiryIso6391LanguageCode;
  selectedComponentAndStep: SelectedComponentAndStep;
  workflowVersionMap?: WorkflowVersionMap | undefined;
  submissionComponentErrors?: ComponentValidationErrorsMap | undefined;
  handleLoadStepLocalizedStrings: (
    stepNames?: Array<string>,
    locales?: Array<InquiryIso6391LanguageCode>,
  ) => Promise<void>;
  handleSetLocale: (locale: InquiryIso6391LanguageCode) => void;
  handleUpdateFormValues: ({
    steps,
    fieldSchema,
  }: Pick<FlowTreeFormValues, 'steps' | 'fieldSchema'>) => void;
  showShortcutsModal?: () => void;
  setSelectedComponentAndStep: (selection: SelectedComponentAndStep) => void;
}

enum ScreenMode {
  Screens = 'screens',
  Components = 'components',
}

type Params = { stepName?: string; combinedStepScreenNameOrComponentName?: string };

const InquiryTemplateScreenEditor = ({
  inquiryTemplate,
  inquiryTemplateVersion,
  stepsPreviewMap,
  pages,
  currentLocale,
  baseRoutePath,
  workflowVersionMap,
  submissionComponentErrors,
  selectedComponentAndStep,
  handleLoadStepLocalizedStrings,
  handleUpdateFormValues,
  handleSetLocale,
  showShortcutsModal,
  setSelectedComponentAndStep,
}: InquiryTemplateScreenEditorProps) => {
  const {
    'field-schema': fieldSchema,
    'enabled-locales': enabledLocales,
    steps,
    theme,
  } = inquiryTemplateVersion.attributes;
  const [activeScrollItemId, setActiveScrollItemId] = useState<string | undefined>(undefined);
  const [selectedStep, setRawSelectedStep] = useState<SelectedStep | undefined>(undefined);
  const [fieldsToDelete, setFieldsToDelete] = useState<string[]>([]);
  const [deviceHandoffCombinedStep, setDeviceHandoffCombinedStep] = useState<
    InquiryTemplateVersionCombinedStep | undefined
  >(steps.filter(isCombinedStepConfig)[0]);

  const [showLeftNavPanel, { toggle: toggleLeftNavPanel }] = useToggle(true);
  const [showRightNavPanel, { toggle: toggleRightNavPanel, enable: enableRightNavPanel }] =
    useToggle(true);
  const [screenMode, setScreenMode] = useLocalStorage(
    LocalStorageKey.DashboardITEScreenEditorSelectedScreenMode,
    ScreenMode.Screens,
  );

  const selectedComponentToDelete = useRef<{
    componentSource: SelectedComponentAndStep;
    isActiveComponentOnScreen: boolean;
  }>(null);

  const [
    isConfirmDeleteComponentModalOpen,
    { enable: openConfirmDeleteComponentModal, disable: closeConfirmDeleteComponentModal },
  ] = useToggle();

  const [
    isConfirmDeleteFieldOpen,
    { enable: openConfirmDeleteFieldModal, disable: closeConfirmDeleteFieldModal },
  ] = useToggle();

  const screenEditorRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const editableUngated = useIsEditableWithOptions();

  const hasAccessTier = useHasAccessForTierDeterminator();

  const [elementIsBelowBreak] = useIsElementBelowSize({
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    element: screenEditorRef.current,
    breakpoint: MAX_USABLE_EDITOR_WIDTH,
  });

  const [copiedComponentData, setCopiedComponentData] = useLocalStorage<ComponentCopyData>(
    LocalStorageKey.DasbhoardITEScreenEditorCopiedComponent,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    null,
  );
  useEffect(() => {
    const onStorageChange = (e: StorageEvent) => {
      if (e.key === LocalStorageKey.DasbhoardITEScreenEditorCopiedComponent) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const parsedValue = JSON.parse(e.newValue) as ComponentCopyData;
        parsedValue && setCopiedComponentData(parsedValue);
      }
    };
    window.addEventListener('storage', onStorageChange);
    return () => {
      window.removeEventListener('storage', onStorageChange);
    };
  }, [setCopiedComponentData]);

  const previewRef = useRef<HTMLDivElement>(null);
  const screenDimensions = useSelector<RootState, ScreenDimensions>((state: RootState) =>
    getScreenDimensionsLiveUpdated(state),
  );

  useEffect(() => {
    if (!previewRef.current) return;
    const resizeObserver = new ResizeObserver(
      debounce(300, (entries) => {
        // Extract the content rect from the first observer entry, which is the parent container
        // https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver/ResizeObserver
        const parentContainer = entries?.[0]?.contentRect;
        if (parentContainer) {
          const maxScreenDimensions = scaleScreenDimensionsByFactor(
            {
              width: parentContainer.width,
              height: parentContainer.height,
            },
            SCREEN_CONTAINER_TO_PARENT_RATIO,
          );
          dispatch(updateMaxEditorScreenDimensions(screenDimensions, maxScreenDimensions));
        }
      }),
    );
    resizeObserver.observe(previewRef.current);
    return () => {
      resizeObserver.disconnect();
      // clear max screen dimensions when leaving screen editor so that flow editor dimensions aren't restricted
      dispatch(clearMaxEditorScreenDimensions());
    };
  }, [previewRef, dispatch, screenDimensions]);

  const { stepName, combinedStepScreenNameOrComponentName } = useParams<Params>() as Params;
  const navigate = useNavigate();
  const setSelectedStep = (selectedStep: SelectedStep | undefined) => {
    // keep the url synced to the selected step
    if (selectedStep?.stepToken) {
      navigate(
        generatePath(SCREEN_EDITOR_STEP_PATH, {
          inquiryTemplateId: inquiryTemplate.id,
          inquiryTemplateVersionId: inquiryTemplateVersion.id,
          stepName: selectedStep?.stepToken,
        }),
        { replace: true },
      );
    }
    setRawSelectedStep(selectedStep);
  };
  const [searchParams] = useSearchParams();
  const displayFocusParam = searchParams.get(DISPLAY_FOCUS_SEARCH_PARAM);

  const onStepSelected = useCallback(
    (step: SelectedStep) => {
      setSelectedStep(step);
      setSelectedComponentAndStep(null);
      enableRightNavPanel();
    },
    [enableRightNavPanel, setSelectedComponentAndStep],
  );

  const getStepLocalizedStrings = useCallback(
    (locales: Array<InquiryIso6391LanguageCode>) =>
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleLoadStepLocalizedStrings(selectedStep?.stepToken && [selectedStep?.stepToken], locales),
    [selectedStep, handleLoadStepLocalizedStrings],
  );

  const useGovIdRedesign =
    inquiryTemplateVersion.attributes['govid-design-version'] === GOVERNMENT_ID_DESIGN_VERSION.V1;
  const stepsPreview = Object.values(stepsPreviewMap);
  const screenItemConfigs: ScreenItemConfig[] = useMemo(
    () => mapToNavItemConfig(steps, useGovIdRedesign, stepsPreview),
    [steps, useGovIdRedesign, stepsPreview],
  );

  useEffect(() => {
    if (selectedComponentAndStep?.step != null) {
      const updatedConfig = screenItemConfigs.find(
        (config) => config.stepToken === selectedComponentAndStep.step.name,
      );

      if (updatedConfig == null) {
        captureMessage('updatedConfig is null for navigating to selected step', 'warning');
        return;
      }

      // prevent updates unless we're on a different screen
      if (updatedConfig.stepToken !== selectedStep?.stepToken) {
        // skip setting the url since it should already be set when changing selectedComponentAndStep and
        // we don't want to potentially clear the component in the url
        setRawSelectedStep(updatedConfig);
        setActiveScrollItemId(updatedConfig.key);
      }
    }
  }, [selectedComponentAndStep?.step, screenItemConfigs, selectedStep]);

  // Sets the selectedStep/selectedComponentAndStep based on the URL path.
  // NOTE (Fawn): This useEffect needs to happen _after_ the above useEffect
  // since this one should take precedence in how the selectedStep is set.
  useEffect(
    () => {
      // no need to run this if the current selected step/component already aligns with the URL
      const isSelectionAligned =
        combinedStepScreenNameOrComponentName == null
          ? stepName === selectedStep?.stepToken || stepName === selectedComponentAndStep?.step.name
          : stepName === selectedComponentAndStep?.step.name &&
            combinedStepScreenNameOrComponentName === selectedComponentAndStep?.component.name;
      if (!isSelectionAligned) {
        const step = steps.find(({ name }) => name === stepName);
        const uiComponentConfigFromPath =
          isUiStepConfig(step) &&
          uiStepAllComponentConfigs(step).find(
            ({ name }) => name === combinedStepScreenNameOrComponentName,
          );

        // If the URL references a component and we can find it, then navigate to it.
        // Otherwise, find the referenced screen and navigate to that instead.
        if (uiComponentConfigFromPath) {
          setSelectedComponentAndStep({ step, component: uiComponentConfigFromPath, error: false });
          // Set the applicable tab open based on the 'displayFocus' search param
          if (displayFocusParam && isSelectedComponentTab(displayFocusParam)) {
            setComponentPanelSetActiveTab({
              componentPanelIsOpen: false,
              componentName: uiComponentConfigFromPath.name,
              stepName: step.name,
              tab: displayFocusParam,
            });
          }
        } else {
          const initialConfig = screenItemConfigs.find((config) => {
            // NOTE: screenName and combinedStepKey attributes on config only exists for combined step configs
            // We also check if the stepName is one of the accepted step key types
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            if (combinedStepScreenNameOrComponentName != null && config?.pageKeys?.length > 0) {
              const { key, pageKeys } = config;
              // first page key is always the combined step page's primary page.
              // only selfie capture have multiple page keys (capture, pending)
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              const screenName = pageKeys[0];
              const stepNameRegex = new RegExp(`-${screenName}(-.*)*$`);
              const derivedStepNameFromKey = key.replace(stepNameRegex, '');
              return (
                derivedStepNameFromKey === stepName &&
                combinedStepScreenNameOrComponentName === screenName
              );
            }
            return config.stepToken === stepName;
          });
          setSelectedComponentAndStep(null);
          setSelectedStep(initialConfig ?? screenItemConfigs[0]);

          if (
            initialConfig &&
            !isUiStepConfig(steps.find((step) => step.name === initialConfig.stepToken))
          ) {
            setScreenMode(ScreenMode.Screens);
          }
        }
      }
    },
    // We want this useEffect to run once on load, and whenever the url changes to ensure we are synced with the url
    [stepName, combinedStepScreenNameOrComponentName],
  );

  const screenPanelNavItems = useMemo<ReactNode[]>(() => {
    return screenItemConfigs.map((config) => {
      if (config.items) {
        const navGroupItems: GroupScrollItemProps[] = config.items.map((item) => {
          return {
            id: item.key,
            label: item.displayName,
            openWithGroup: true,
            withHighlight: true,
            withInstantScroll: true,
            setActiveScrollItemId: setActiveScrollItemId,
            onClick: () => {
              onStepSelected(item);
            },
          };
        });
        return (
          <EditorNavItemGroup
            key={config.key}
            id={config.key}
            label={config.displayName}
            items={navGroupItems}
            activeScrollItemId={activeScrollItemId}
            setActiveScrollItemId={setActiveScrollItemId}
          />
        );
      } else {
        const stepIndex = steps.findIndex(
          // (1) UI steps will match on stepToken
          // (2) Combined steps can match on
          // - stepToken OR
          // - if stepToken is 'no-configs', will match on key regex without screen name
          // front-end/dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils.ts:237
          (step) =>
            step.name === config.stepToken ||
            step.name === config.key.replace(`-${config?.pageKeys?.[0]}`, ''),
        );

        const stepConfig = steps[stepIndex];

        const placeholder = stepConfig?.['name-display'] ?? '';

        const { LogicIcon, totalCount } = uiStepTotalNumComponentsWithRulesLogicOnConfig(
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          isUiStepConfig(stepConfig) ? stepConfig : undefined,
        );

        const LogicTooltip = (
          <Tooltip side='top' interactive iconTrigger={<LogicIcon />}>
            <Paragraph size='sm' style={{ maxWidth: '260px', whiteSpace: 'pre-wrap' }}>
              This screen contains components that have properties configured with JSONLogic rules.
            </Paragraph>
          </Tooltip>
        );

        return (
          <ScreenNavContainer
            key={config.key}
            id={config.key}
            label={config.displayName}
            stepIndex={stepIndex}
            placeholder={placeholder}
            editDisabled={!editableUngated}
            isActive={config.key === selectedStep?.key}
            setActiveScrollItemId={setActiveScrollItemId}
            onClick={() => {
              onStepSelected(config);
            }}
            withInstantScroll
            withHighlight
            {...(totalCount > 0 && {
              suffixIcon: LogicTooltip,
            })}
          />
        );
      }
    });
  }, [
    activeScrollItemId,
    screenItemConfigs,
    selectedStep?.key,
    steps,
    editableUngated,
    onStepSelected,
  ]);

  const { basePath, clientData, currStep } = useMemo<{
    basePath: string;
    clientData: StepPreview | null;
    currStep: InquiryTemplateVersionStep | null;
  }>(() => {
    const stepName = isDeviceHandoffPageKey(selectedStep?.stepToken)
      ? deviceHandoffCombinedStep?.name
      : selectedStep?.stepToken;
    const stepIndex = steps.findIndex((step) => step.name === stepName);
    if (stepIndex === -1) {
      return { basePath: '', clientData: null, currStep: null };
    }
    return {
      basePath: `${stepIndex}`,
      clientData: stepsPreviewMap[stepName as string],
      currStep: steps[stepIndex],
    };
  }, [deviceHandoffCombinedStep, selectedStep, steps, stepsPreviewMap]);

  const updateScreenEditorFormValues = useCallback(
    ({
      updatedStep,
      component,
      fields,
    }: {
      updatedStep: InquiryTemplateVersionStep;
      component?: UiComponentConfig;
      fields?: FieldSchema[];
    }) => {
      const updatedSteps: InquiryTemplateVersionStep[] = steps.map((step) =>
        step.name === updatedStep.name ? updatedStep : step,
      );
      handleUpdateFormValues({ steps: updatedSteps, fieldSchema: fields });
      if (isUiStepConfig(updatedStep) && component != null) {
        setSelectedComponentAndStep({
          component,
          step: updatedStep,
          error: false,
          newComponent: true,
        });
      }
    },
    [
      dispatch,
      steps,
      handleUpdateFormValues,
      setSelectedComponentAndStep,
      currentLocale,
      inquiryTemplateVersion,
    ],
  );

  const deleteCurrentComponent = useCallback(() => {
    if (selectedComponentToDelete.current == null) return;
    const { componentSource, isActiveComponentOnScreen } = selectedComponentToDelete.current;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const { step } = componentSource;
    const component = getCurrentComponent(steps, componentSource);
    let updatedStep: InquiryTemplateVersionStepUi;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    let componentsToDelete: UiComponentConfig[] = [component];
    if (isUiParentComponentConfig(component)) {
      updatedStep = unnestComponentsAtPath({
        component,
        step,
        filterPredicate: (componentConfig: UiComponentConfig) =>
          NONDELETABLE_COMPONENTS.includes(componentConfig.type),
      });
      // concat deletable child components to ensure that single-reference
      // fields are also cleaned up when the parent component is deleted.
      componentsToDelete = componentsToDelete.concat(
        component.config.children.filter(
          (componentConfig) => !NONDELETABLE_COMPONENTS.includes(componentConfig.type),
        ),
      );
    } else {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      updatedStep = removeComponentFromPath(component, step);
    }

    let fieldKeysToRemove: string[] = [];
    const stepIndex = steps.findIndex((step) => step.name === updatedStep.name);
    const updatedSteps = produce(steps, (draftSteps) => {
      draftSteps[stepIndex] = updatedStep;
    });
    // build the reference map inline using updated steps to ensure the reference count is up to date
    const fieldReferenceMap = buildFieldReferenceMap({
      fieldSchema: fieldSchema,
      steps: updatedSteps,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      workflowVersionMap: workflowVersionMap,
      vtmplVersionMap: {},
    });
    componentsToDelete.filter(isUiFormComponentConfig).forEach((component) => {
      const fieldKeys = isUiFormMultiFieldComponentConfig(component)
        ? getComponentFieldKeys(component).map(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            (fieldAttributeKey: keyof InputMultiFieldComponentFieldConfig) =>
              component.config[fieldAttributeKey],
          )
        : [component.config.field];
      // concat field keys with no remaining references to be filtered out of fieldSchema
      fieldKeysToRemove = fieldKeysToRemove.concat(
        fieldKeys
          .map((keyValue) => (fieldReferenceMap[keyValue]?.length === 0 ? keyValue : null))
          .filter((s): s is string => s != null),
      );
    });

    // If the component we're deleting is also the active component on screen
    // clear the selection
    if (isActiveComponentOnScreen) {
      setSelectedComponentAndStep(null);
    }
    updateScreenEditorFormValues({
      updatedStep,
    });
    if (fieldKeysToRemove.length > 0) {
      setFieldsToDelete(fieldKeysToRemove);
      openConfirmDeleteFieldModal();
    }
  }, [
    fieldSchema,
    openConfirmDeleteFieldModal,
    selectedComponentToDelete,
    setSelectedComponentAndStep,
    steps,
    updateScreenEditorFormValues,
    workflowVersionMap,
  ]);

  const handleComponentDelete = useCallback(
    (clickedComponentAndStep?: SelectedComponentAndStep) => {
      if (selectedComponentAndStep == null && clickedComponentAndStep == null) return;
      // we can pass in a component information to delete, or by default delete the currently-selected component
      const componentSource = clickedComponentAndStep ?? selectedComponentAndStep;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      selectedComponentToDelete.current = {
        componentSource,
        isActiveComponentOnScreen: isEqual(clickedComponentAndStep, selectedComponentAndStep),
      };
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const { step } = componentSource;
      const component = getCurrentComponent(steps, componentSource);

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (NONDELETABLE_COMPONENTS.includes(component.type)) {
        dispatch(
          showMessage('Cannot delete this component from the template', MessageType.Failure),
        );
        return;
      }

      // disallow deleting Footer component when it contains non-deletable components
      if (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        component.type === UiComponentConfigType.Footer &&
        component.config.children.some((childComponent) =>
          NONDELETABLE_COMPONENTS.includes(childComponent.type),
        )
      ) {
        dispatch(
          showMessage(
            'Footer contains Persona branding and cannot be removed from this template',
            MessageType.Failure,
          ),
        );
        return;
      }

      // disallow moving BIPA submit triggers from templates
      if (isCurrentComponentBIPASubmitTrigger({ step, component })) {
        dispatch(
          showMessage(
            'Cannot delete this button from the template due to BIPA compliance',
            MessageType.Failure,
          ),
        );
        return;
      }

      if (
        isUiTransitionComponentConfig(component) &&
        isTransitionComponentTargetSet(component) &&
        !duplicateTransitionComponentTargetExists(component, step)
      ) {
        openConfirmDeleteComponentModal();
        return;
      }

      deleteCurrentComponent();
    },
    [
      selectedComponentAndStep,
      dispatch,
      steps,
      deleteCurrentComponent,
      openConfirmDeleteComponentModal,
    ],
  );

  const handleComponentCopy = useCallback(
    (displaySuccessToast: boolean) => {
      if (selectedComponentAndStep == null || clientData == null) return;

      const step = selectedComponentAndStep?.step ?? selectedStepConfig;
      const component = getCurrentComponent(steps, selectedComponentAndStep);

      const copyErrorMessage = copyComponentErrorMessage(component);

      if (copyErrorMessage) {
        dispatch(showMessage(copyErrorMessage, MessageType.Failure));
        return;
      }

      setCopiedComponentData({
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        component: configureComponentForCopy(step, component, clientData as StepUi),
        inquiryTemplateVersionId: inquiryTemplateVersion.id,
      });
      if (displaySuccessToast) {
        dispatch(showMessage('Successfully copied component to clipboard.', MessageType.Success));
      }
    },
    [selectedComponentAndStep, dispatch, steps, clientData],
  );

  const handleComponentPaste = useCallback(() => {
    if (copiedComponentData == null) return;
    const step = selectedComponentAndStep?.step ?? selectedStepConfig;
    const selectedComponent = selectedComponentAndStep?.component;

    const pasteErrorMessage = pasteComponentErrorMessage({
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      step,
      selectedComponent,
      copiedComponentData,
      inquiryTemplateVersionId: inquiryTemplateVersion.id,
      hasAccessTier,
    });

    if (pasteErrorMessage) {
      dispatch(showMessage(pasteErrorMessage, MessageType.Failure));
      return;
    }

    const { configArray, configArrayPath, pasteIndex } = getPasteInsertionConfigs(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      step,
      copiedComponentData.component,
      selectedComponent,
    );
    const pasteConfiguredComponentConfig = configureComponentForPaste(
      fieldSchema.map(({ key }) => key),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      step,
      copiedComponentData.component,
    );
    const pasteConfiguredFieldSchema = configureFieldSchemaForPaste(
      fieldSchema,
      pasteConfiguredComponentConfig,
    );

    const updatedStep = addComponentsAtIndex({
      index: pasteIndex,
      componentConfigs: [pasteConfiguredComponentConfig],
      configArray,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      step,
      path: configArrayPath,
    });

    updateScreenEditorFormValues({
      updatedStep,
      component: pasteConfiguredComponentConfig,
      fields: fieldSchema.concat(pasteConfiguredFieldSchema),
    });

    const updatedComponentConfigPath = findComponentConfigPath(
      (component: UiComponentConfig) => component.name === pasteConfiguredComponentConfig.name,
      updatedStep.config.components,
    );
    if (updatedComponentConfigPath) {
      dispatch(
        refreshComponentPreview(
          updatedStep.name,
          updatedComponentConfigPath.join('.'),
          pasteConfiguredComponentConfig,
        ),
      );
    }
  }, [
    selectedComponentAndStep,
    fieldSchema,
    dispatch,
    steps,
    selectedStep,
    copiedComponentData,
    updateScreenEditorFormValues,
  ]);

  // reselects selectedStep from component updates
  // (1) Component is deleted
  // (2) Undo button is pressed (component no longer exists within step)
  useEffect(() => {
    if (selectedComponentAndStep != null) {
      const componentInStep = getCurrentComponent(steps, selectedComponentAndStep);

      if (componentInStep == null) {
        setSelectedComponentAndStep(null);
      }
    }
  }, [steps, selectedComponentAndStep, setSelectedComponentAndStep]);

  // handle delete unless inside of a textbox
  useKeydownDeleteListener((_) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (PERMITTED_DOCUMENT_TAGS.includes(document.activeElement?.tagName)) {
      handleComponentDelete();
    }
  });

  // handle component copy unless inside of a textbox
  useKeyDownPasteListener((event) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (PERMITTED_DOCUMENT_TAGS.includes(document.activeElement?.tagName)) {
      handleComponentPaste();
      event.preventDefault();
    }
  });

  // handle component paste unless inside of a textbox
  useKeydownCopyListener((_) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (PERMITTED_DOCUMENT_TAGS.includes(document.activeElement?.tagName)) {
      handleComponentCopy(true);
    }
  });

  const selectedStepConfig: InquiryTemplateVersionStepUi | null = useMemo(() => {
    const stepConfig = steps.find((step) => step.name === selectedStep?.stepToken);
    return isUiStepConfig(stepConfig) ? stepConfig : null;
  }, [selectedStep, steps]);

  const screenItemsRef = useRef();
  const componentItemsRef = useRef();
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [hasScrolledScreenNavContainer] = useHasScrolled(screenItemsRef);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [hasScrolledComponentNavContainer] = useHasScrolled(componentItemsRef);

  const componentCanBeCopied = useMemo((): boolean => {
    if (clientData == null) {
      return false;
    }

    if (selectedComponentAndStep == null) return false;

    return copyComponentErrorMessage(getCurrentComponent(steps, selectedComponentAndStep)) == null;
  }, [selectedComponentAndStep, steps, clientData]);

  const componentCanBePasted = useMemo((): boolean => {
    if (copiedComponentData == null) return false;

    return (
      pasteComponentErrorMessage({
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        step: selectedComponentAndStep?.step ?? selectedStepConfig,
        selectedComponent: selectedComponentAndStep?.component,
        copiedComponentData,
        inquiryTemplateVersionId: inquiryTemplateVersion?.id,
        hasAccessTier,
      }) == null
    );
  }, [selectedComponentAndStep, copiedComponentData, hasAccessTier, inquiryTemplateVersion]);

  const ScreenNavigation = (
    <>
      <ScreenPanelHeader hasScrolledContent={hasScrolledScreenNavContainer}>
        <ScreenPanelHeaderInner>
          <ScreensPanelHeading>Screens</ScreensPanelHeading>
          <SelectWrapper data-cy='screen-editor__locale-select'>
            <Select
              size='sm'
              value={currentLocale}
              onValueChange={(value) => handleSetLocale(value as InquiryIso6391LanguageCode)}
            >
              {enabledLocales.map((locale) => (
                <Option key={locale} value={locale}>
                  {INQUIRY_LANGUAGES_ENGLISH[locale]}
                </Option>
              ))}
            </Select>
          </SelectWrapper>
        </ScreenPanelHeaderInner>
      </ScreenPanelHeader>
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <NavItemContainer overflowScroll={Boolean(selectedStep)} ref={screenItemsRef}>
        {screenPanelNavItems}
      </NavItemContainer>
    </>
  );

  return (
    <RootContainer ref={screenEditorRef}>
      <ConfirmDeleteTransitionComponentModal
        isOpen={isConfirmDeleteComponentModalOpen}
        handleClose={closeConfirmDeleteComponentModal}
        handleConfirmDelete={() => {
          deleteCurrentComponent();
          closeConfirmDeleteComponentModal();
        }}
      />
      <ConfirmDeleteFieldModal
        isOpen={isConfirmDeleteFieldOpen}
        handleClose={() => {
          closeConfirmDeleteFieldModal();
          setFieldsToDelete([]);
        }}
        handleConfirmDelete={() => {
          closeConfirmDeleteFieldModal();
          // FIXME: Removing the fields in a separate update form call causes two events to be pushed to the undo stack
          handleUpdateFormValues({
            fieldSchema: fieldSchema.filter(({ key }) => !fieldsToDelete.includes(key)),
          });
          setFieldsToDelete([]);
        }}
        fieldsToDelete={fieldsToDelete}
      />
      {elementIsBelowBreak && <UnusuableScreenEditorWarning />}
      <InquiryTemplateEditorToolbar
        leftNavPanel={{
          sidePanelVisible: showLeftNavPanel,
          setSidePanelVisible: toggleLeftNavPanel,
        }}
        rightNavPanel={{
          sidePanelVisible: showRightNavPanel,
          setSidePanelVisible: toggleRightNavPanel,
        }}
        showShortcutsModal={showShortcutsModal}
      />
      <ContentContainer>
        <ScreensPanelContainer data-test='ite-screens__panel-container' visible={showLeftNavPanel}>
          <ScreenEditorLeftContainer>
            <>
              <StyledTabBar>
                <Tab
                  id='inquiry-template-screen-editor__navigation-tab'
                  selected={screenMode === ScreenMode.Screens}
                  onClick={() => setScreenMode(ScreenMode.Screens)}
                >
                  Navigation
                </Tab>
                {editableUngated && (
                  <Tab
                    id='inquiry-template-screen-editor__components-tab'
                    selected={screenMode === ScreenMode.Components}
                    onClick={() => setScreenMode(ScreenMode.Components)}
                  >
                    Component Library
                  </Tab>
                )}
              </StyledTabBar>
              {screenMode === ScreenMode.Screens && (
                <>
                  <ScreenNavigationContainer>{ScreenNavigation}</ScreenNavigationContainer>
                  <ScreenComponentsSection>
                    <ScreenPanelHeader hasScrolledContent={hasScrolledComponentNavContainer}>
                      <ScreenPanelHeaderInner>
                        <ScreensPanelHeading>Components</ScreensPanelHeading>
                        {editableUngated && (
                          <Tooltip
                            interactive={false}
                            trigger={
                              <Button
                                width='icon'
                                size='sm'
                                disabled={selectedStepConfig == null}
                                variant='basic'
                                id='ite-screen-editor__add-new-component-button'
                                onClick={() => {
                                  setScreenMode(ScreenMode.Components);
                                }}
                              >
                                <PlusIcon />
                              </Button>
                            }
                            side='right'
                          >
                            Add new component
                          </Tooltip>
                        )}
                      </ScreenPanelHeaderInner>
                    </ScreenPanelHeader>
                    {selectedStepConfig == null && (
                      <ComponentItemTextContainer>
                        <Paragraph color='secondary' size='sm'>
                          New components cannot be added to this screen
                        </Paragraph>
                      </ComponentItemTextContainer>
                    )}
                    {selectedStepConfig != null && (
                      <>
                        {selectedStepConfig.config.components.length === 0 && (
                          <ComponentItemTextContainer>
                            <Paragraph color='secondary' size='sm'>
                              Click the &quot;Add new component&quot; button or &quot;Library&quot;
                              tab to add components
                            </Paragraph>
                          </ComponentItemTextContainer>
                        )}
                        {selectedStepConfig.config.components.length > 0 && (
                          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                          <ComponentItemContainer ref={componentItemsRef}>
                            <ScreenComponentList
                              selectedStepConfig={selectedStepConfig}
                              selectedComponent={selectedComponentAndStep?.component?.name}
                              setSelectedComponentAndStep={setSelectedComponentAndStep}
                              handleComponentDelete={handleComponentDelete}
                            />
                          </ComponentItemContainer>
                        )}
                      </>
                    )}
                  </ScreenComponentsSection>
                </>
              )}
              {screenMode === ScreenMode.Components && (
                <ScreenEditorComponentLibrary
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  isInitialStep={isInitialStep(currStep)}
                />
              )}
            </>
          </ScreenEditorLeftContainer>
        </ScreensPanelContainer>
        <ScreenPreviews
          screenItemConfigs={screenItemConfigs.flatMap((config) =>
            config.items ? config.items : config,
          )}
          stepsConfigMap={keyBy('name', steps)}
          stepsPreviewMap={stepsPreviewMap}
          previewPageMap={keyBy(
            'key',
            pages.concat(
              deviceHandoffPages({
                stepsPreview,
                currentCombinedStepName: deviceHandoffCombinedStep?.name,
              }),
            ),
          )}
          selectedStep={selectedStep}
          selectedComponentAndStep={selectedComponentAndStep}
          fieldSchema={fieldSchema}
          theme={theme}
          screenDimensions={screenDimensions}
          onComponentClick={(componentAndStep) => {
            setSelectedComponentAndStep(componentAndStep);
            enableRightNavPanel();
          }}
          onStepSelected={onStepSelected}
          updateSteps={updateScreenEditorFormValues}
        />
        {selectedStep && (
          <ControlPanelContainer visible={showRightNavPanel} id={SCREEN_EDITOR_CONTROL_PANEL_ID}>
            <>
              {((isUiStepConfig(currStep) && selectedComponentAndStep?.component == null) ||
                isIntegrationStepConfig(currStep)) && (
                <ScreenSectionsPanel
                  selectedStep={selectedStep}
                  currentStep={currStep}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  stepPreview={clientData}
                  availableBackSteps={backStepTargetOptions({ steps, currentStep: currStep })}
                  basePath={basePath}
                  theme={theme}
                  editable={editableUngated}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  handleComponentPaste={componentCanBePasted && handleComponentPaste}
                  handleClosePanel={toggleRightNavPanel}
                  updateSteps={updateScreenEditorFormValues}
                />
              )}
              {isUiStepConfig(currStep) && selectedComponentAndStep != null && (
                <ComponentPanel
                  currentStep={currStep}
                  selectedComponentAndStep={selectedComponentAndStep}
                  basePath={basePath}
                  baseRoutePath={baseRoutePath}
                  clientData={clientData as StepUi}
                  currentLocale={currentLocale}
                  enabledLocales={enabledLocales}
                  steps={steps}
                  editable={editableUngated}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  workflowVersionMap={workflowVersionMap}
                  submissionComponentErrors={submissionComponentErrors}
                  fieldSchema={fieldSchema}
                  theme={theme}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  getStepLocalizedStrings={getStepLocalizedStrings}
                  handleClosePanel={toggleRightNavPanel}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  handleComponentPaste={componentCanBePasted && handleComponentPaste}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  handleComponentCopy={componentCanBeCopied && handleComponentCopy}
                  handleComponentDelete={handleComponentDelete}
                  setSelectedComponentAndStep={setSelectedComponentAndStep}
                  updateSteps={updateScreenEditorFormValues}
                />
              )}
              {isCombinedStepConfig(currStep) &&
                !isDeviceHandoffPageKey(selectedStep?.stepToken) && (
                  <CombinedStepPanel
                    step={currStep}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    stepPreview={clientData}
                    availableBackSteps={backStepTargetOptions({ steps, currentStep: currStep })}
                    basePath={basePath}
                    title={selectedStep.displayName}
                    currentLocale={currentLocale}
                    enabledLocales={enabledLocales}
                    pageKeys={
                      /* @ts-expect-error TODO (eric): fix type - 'check page' non-overlapping with other localization keys */
                      selectedStep.pageKeys as Array<keyof CombinedStepConfig['localizations']>
                    }
                    editableUngated={editableUngated}
                    fieldSchema={fieldSchema}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    getStepLocalizedStrings={getStepLocalizedStrings}
                    updateSteps={updateScreenEditorFormValues}
                  />
                )}
              {isDeviceHandoffPageKey(selectedStep?.stepToken) &&
                deviceHandoffCombinedStep != null &&
                isCombinedStep(stepsPreviewMap[deviceHandoffCombinedStep.name]) && (
                  <DeviceHandoffPanel
                    basePath={basePath}
                    title={selectedStep.displayName}
                    step={deviceHandoffCombinedStep}
                    stepPreview={
                      stepsPreviewMap[deviceHandoffCombinedStep.name] as CombinedStepClientData
                    }
                    steps={steps}
                    pageKey={selectedStep.stepToken}
                    currentLocale={currentLocale}
                    enabledLocales={enabledLocales}
                    editableUngated={editableUngated}
                    handleUpdateFormValues={handleUpdateFormValues}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    getStepLocalizedStrings={getStepLocalizedStrings}
                    setDeviceHandoffCombinedStep={setDeviceHandoffCombinedStep}
                  />
                )}
            </>
          </ControlPanelContainer>
        )}
      </ContentContainer>
    </RootContainer>
  );
};

export default InquiryTemplateScreenEditor;
